import { ListingBadges, ListingCard, utils } from '@adc/ui-adc'
const { symbols } = utils

const LISTING_BADGES_REFERENCE =
  ListingBadges[symbols.GRAPH_API_FRAGMENT_REFERENCE]
const LISTING_BADGES_FRAGMENT =
  ListingBadges[symbols.GRAPH_API_FRAGMENT_DEFINITION]

const LISTING_CARDS_REFERENCE =
  ListingCard[symbols.GRAPH_API_FRAGMENT_REFERENCE]
const LISTING_CARDS_FRAGMENT =
  ListingCard[symbols.GRAPH_API_FRAGMENT_DEFINITION]

const ADDRESS_FRAGMENT = `
fragment Address on Address {
  street_number
  street_name
  street_unit
  country_primary_subdivision
  country_secondary_subdivision
  country_tertiary_subdivision
  municipality
  postal_code
  coordinates {
    lat
    lon
  }
}`

const AUCTION_DETAILS_BOX_REFERENCE = '...AuctionDetailsBoxFragment'
const AUCTION_DETAILS_BOX_FRAGMENT = `
  fragment AuctionDetailsBoxFragment on Listing {
    reserve_price
    is_hot
    auction {
      auction_date
      start_date_time
      end_date
      end_date_time
      starting_bid
      provisional_sale_date
      is_outbid_active
      is_online
      bid_instruction {
        nos_amount
      }
    }
    trustee {
      sale_time
      online_state_deposit_amount
      partnership_details
    }
    listing_configuration {
      sale_instruction
      total_estimated_debt
      final_judgement_amount
      occupancy_status
      principal_original_amount
      trustee_sales_agent
      postponement_new_auction_date
      postponement_new_auction_start_date
      postponement_new_auction_end_date
      postponement_new_auction_time
      minimum_credit_bid_amount
      trustee_sales_channel
      is_third_party_online
      is_reserve_displayed
      is_proxy_bidding_enabled
      asset_type
      are_private_tours_available
      is_minimum_bid_enabled
    }
    marketing_attributes(resolveTags: true, attributeClasses: [FEATURE_TOGGLE, CONTENT] ) {
      ... on FeatureToggleMarketingAttribute {
        attribute_type
        is_enabled
      }

      ... on ContentMarketingAttribute {
        default_display_value
        attribute_type
        show
        label
        message
       }
    }
    venue {
      venue_id
      venue_code
      venue_title
      venue_name
      venue_url
      venue_location_directions
      venue_type
      venue_address {
        ...Address
      }
      venue_schedule {
        initial_bidding_end_time
        bid_start_local_date
      }
      external_identifiers {
        external_identifier
        data_source
      }
    }
  }
`

const DUE_DILIGENCE_REFERENCE = '...DocumentsFragment'
const DUE_DILIGENCE_FRAGMENT = `
  fragment DocumentsFragment on Listing {
    listing_configuration {
      financing_available
      title_insurance
    }

    program_configuration {
      deed_types
    }

    fcl_marketing {
      appraiser_uri
      clerk_of_court_uri
    }

    documents(filters: {
      document_usage_type_codes: [WEB]
    }) {
      document_external_title
      updated_date
      metadata {
        document_purpose_type_code
      }
      file_descriptor {
        urls {
          cdn
          download
        }
      }
    }

    applicable_global_documents(filters: {
      media_usage_type_codes: [WEB]
    }) {
      priority_level_type_code
      media_purpose_type_code
      media_content_type
      media_download_uri
      media_file_name
      media_internal_title
      media_external_title
      media_desc
      updated_date
      __typename
    }
  }
`

const PROPERTY_DETAILS_REFERENCE = '...PropertyDetailsFragment'
const PROPERTY_DETAILS_FRAGMENT = `
  fragment PropertyDetailsFragment on Listing {
    trustee_reference_id
    trustee_sale_number
    external_identifiers {
      external_identifier
      data_source
    }
    external_information {
      collateral {
        is_valuation_available
      }
    }
    listing_remarks {
      # Property Description
      mls_comment
      # Disclaimer Field
      legal_description
      # Special Notes
      special_notes
      additional_seller_terms
    }
    primary_property {
      is_currently_saved
      property_id
      land_parcels {
        # APN Number?
        parcel_number
      }
      summary {
        total_bedrooms
        total_bathrooms
        living_size
        year_built
        apn
        hoa_dues_amount
        lot_size
        structure_type_code
        structure_type_group
        valuation
      }
      vendor_report_url
      has_vendor_information_available
    }
    listing_configuration {
      monthly_hoa_amount
    }
  }
`

const PROPERTY_HEADER_REFERENCE = '...PropertyHeaderFragment'
const PROPERTY_HEADER_FRAGMENT = `
  fragment PropertyHeaderFragment on Listing {
    listing_status
    listing_status_group
    listing_status_label
    listing_page_path
    is_coming_soon
    formatted_address(format: DOUBLE_LINE)
    metrics {
      pdp_view_count
    }
    marketing_tags {
      tag
    }
    event {
      title
      # is_trustee
      trustee_sale
      event_type
      # Auction Number
      event_code
      # auction_id
      event_id
      # auction_date
      event_schedule {
        campaign_start_date
      }
    }
    listing_media {
      media_external_title
      media_download_uri
      media_cdn_uri
      updated_date
      media_type
    }
    seller_property {
      street_description
      country_primary_subdivision
      country_secondary_subdivision
      municipality
      postal_code
    }
    primary_property {
      metrics {
        pdp_view_count
      }
      is_currently_saved
      property_id
      summary {
        street_address
        formatted_address
        address {
          ...Address
        }
      }
    }
  }
`

const PDP_LISTING_REFERENCE = '...PDPListing'
const PDP_LISTING_FRAGMENT = `
fragment PDPListing on Listing {
  listing_id
  primary_property_id
  is_published
  is_searchable
  current_listing {
    listing_id
    listing_page_path
  }
  listing_status
  seller_plan {
    seller_plan_code
  }
  seller_terms {
    finance_terms {
      finance_preference
    }
  }
  primary_photo
  photos {
    file_descriptor {
      urls {
        cdn
      }
    }
  }
  parties {
    party_role
    party_name
    party_email
    party_phone
    party_license_number
    party_address {
      postal_code
      street_description
      municipality
      country_primary_subdivision
    }
  }
  short_sale {
    agreement_expiration_date
    is_income_producing
  }

  listing_configuration {
    is_buyers_premium_available
    is_earnest_money_deposit_available
    is_first_look_enabled
    first_look_end_date
  }
  live_auction_segment(resolvePolicy: ORIGIN_ONLY) {
    listing_id
    __typename
    starting_bid_amount
  }
  online_auction_segment(resolvePolicy: ORIGIN_ONLY) {
    listing_id
    __typename
    start_date
    segment_type
    bids_ranking {
      ranking {
        belongs_to_current_user
      }
    }
    initial_end_date
    current_time
    reserve_status
    configuration {
      is_match_bidding_enabled
      is_registration_deposit_required_enabled
      is_max_bidding_enabled
      bid_again_count
      should_bid_again
      is_registration_enabled

    }
    starting_bid_amount
    subject_to_status
    current_highest_bid {
      bid_amount
      type
    }
    segment_status
    current_reserve_amount
    current_increment_amount
    bid_count
    result {
     winning_bid_amount
    }
  }
  program_configuration {
    buyers_premium_minimum_amount
    buyers_premium_primary_amount
    buyers_premium_commission_type

    earnest_money_deposit_minimum_amount
    earnest_money_deposit_primary_amount
    earnest_money_deposit_commission_type

    third_party_title_provider
    program_enrollment_code
  }
  listing_summary {
    is_remote_bid_enabled
    show_opening_bid
  }
  attribution_source {
    origin_code
  }
  intake_source {
    origin_code
  }
  valuation {
    seller_current_value_amount
  }
  strategy {
    selling_method_attributes {
      online_segment_type
    }
  }
}
`

const DIRECT_OFFER_REFERENCE = '...DirectOfferData'
const DIRECT_OFFER_FRAGMENT = `
  fragment DirectOfferData on Listing {
    listing_configuration {
      is_direct_offer_enabled
    }

    offers_summary {
      has_accepted_direct_offer
    }

    venue {
      venue_name
      venue_url
      venue_address {
        street_unit
        street_number
        street_name
        municipality
        country_primary_subdivision
        postal_code
        country_code
      }
      event {
        event_type
        description
      }
      venue_schedule {
        bid_start_local_date
        bid_start_local_time
        initial_bidding_end_local_date
        initial_bidding_end_local_time
      }
    }

    offer_suggestion {
      suggested_offer_low
      suggested_offer_high
    }
  }
`

const RECOMMENDED_LISTING_REFERENCE = '...RecommendedListingFields'
const RECOMMENDED_LISTING_FRAGMENT = `
  fragment RecommendedListingFields on Listing {
    recommended_listings {
      ${LISTING_CARDS_REFERENCE}
    }
  }
`

export const FIELDS_REQUIRING_ACTIVE_SESSION_FRAGMENT = `
  fragment FieldsRequiringActiveSessionFields on Listing {
    is_current_user_first_look_qualified
    is_current_user_registered_for_online_auction

    current_user_online_registration {
      registration_status
    }

    current_user_remote_bid @include(if: $isRemoteBiddingEnabled) {
      remote_bid_amount
    }

    current_user_bid_deposit_requirements {
      is_registration_deposit_applicable
      is_registration_deposit_required_enabled
      absolute_amount_required
    }

    is_current_user_allowed(
      actions: [
        ONLINE_BIDDING_REGISTRATION
        ONLINE_REGULAR_BIDDING
        ONLINE_PROXY_BIDDING
        ONLINE_DIRECT_OFFER
      ]
    ) {
      __typename
      action
      allowed
      reason
      reason_code
    }
    current_user_max_bid {
      max_bid_amount
    }
    online_auction_segment(resolvePolicy: ORIGIN_ONLY) {
      segment_status
      current_user_max_bid {
        max_bid_amount
      }
      bids_ranking {
        ranking {
          belongs_to_current_user
        }
      }
      configuration {
        is_registration_deposit_required_enabled
      }
      current_user_bidding_status
    }
  }
`

const fragments = [
  ADDRESS_FRAGMENT,
  AUCTION_DETAILS_BOX_FRAGMENT,
  DUE_DILIGENCE_FRAGMENT,
  PROPERTY_DETAILS_FRAGMENT,
  PROPERTY_HEADER_FRAGMENT,
  PDP_LISTING_FRAGMENT,
  LISTING_BADGES_FRAGMENT,
  LISTING_CARDS_FRAGMENT,
  RECOMMENDED_LISTING_FRAGMENT,
  DIRECT_OFFER_FRAGMENT,
  FIELDS_REQUIRING_ACTIVE_SESSION_FRAGMENT,
]

const references = [
  AUCTION_DETAILS_BOX_REFERENCE,
  DUE_DILIGENCE_REFERENCE,
  PROPERTY_DETAILS_REFERENCE,
  PROPERTY_HEADER_REFERENCE,
  PDP_LISTING_REFERENCE,
  LISTING_BADGES_REFERENCE,
  RECOMMENDED_LISTING_REFERENCE,
  DIRECT_OFFER_REFERENCE,
]

export const GRAPH_LISTING_QUERY = `
    ${fragments.join()}

  query resiPdp_listing($dsl: String, $hasAuthenticatedUser: Boolean!, $isRemoteBiddingEnabled: Boolean!) {
      seek(type: LISTINGS, dsl: $dsl) {
        content {
          ... on Listing {
            ${references.join()}

            ...FieldsRequiringActiveSessionFields @include(if: $hasAuthenticatedUser)
          }
        }
      }
    }
  `
